import { useCallback, useSyncExternalStore } from 'react';
import { currentTheme, setTheme, type Theme } from './theme';

/**
 * React binding over the theme module.
 *
 * The theme's source of truth is the `data-theme` attribute on <html>
 * (written by setTheme / the boot script). Every hook instance subscribes to
 * that attribute via useSyncExternalStore + MutationObserver, so ALL
 * consumers re-render on a toggle — component-local useState here once left
 * the top-nav logo frozen on the mount-time theme while the page switched.
 */
function subscribe(onChange: () => void): () => void {
  const observer = new MutationObserver(onChange);
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['data-theme'],
  });
  return () => observer.disconnect();
}

export function useTheme(): { theme: Theme; toggle: () => void } {
  const theme = useSyncExternalStore(subscribe, currentTheme);

  const toggle = useCallback(() => {
    setTheme(currentTheme() === 'dark' ? 'light' : 'dark');
  }, []);

  return { theme, toggle };
}
